import { maxDate } from '@pages/StudentDetailsPage/components/common/constants';
import { minDate } from '@pages/RecommenderSpace/components/constants';
import { FormStateErrors, OptionalNullableDateString } from '@constants/commonApp/types';

const isDateValid = (date: string | null | undefined) => {
  return !date?.includes('NaN');
};

const formHelperText: { [key: string]: string } = {
  expectedMidYear: 'Enter a date in the format MM/DD/YYYY',
};

interface DateField {
  label: string;
  value: OptionalNullableDateString;
}

type DateRange = [startDate: OptionalNullableDateString, endDate: OptionalNullableDateString];

export const validateFullDates = <T>(errors: FormStateErrors<T>, dates: DateField[]) => {
  dates.forEach((date) => {
    if (!isDateValid(date.value as string)) {
      errors[date.label as keyof T] = 'Please enter a valid date';
    }

    let dateObj: Date | undefined;

    if (date.value) {
      dateObj = new Date(date.value);

      if (dateObj < minDate) {
        errors[date.label as keyof T] = `Date must be after ${minDate.getFullYear() - 1}`;
      }
    }

    if (dateObj) {
      if (dateObj > maxDate) {
        errors[date.label as keyof T] = `Date must be before ${maxDate.getFullYear() + 1}`;
      }
    }
  });

  return errors;
};

export const validateMonthYearDates = <T>(dates: DateField[]): Partial<FormStateErrors<T>> => {
  const errors = {} as FormStateErrors<T>;
  dates.forEach((date) => {
    if (!isDateValid(date.value)) {
      errors[date.label as keyof T] = 'Please enter a valid date';
    }

    const [dateMonth, dateYear] = date.value?.split('/') ?? [];

    if (dateYear && dateMonth) {
      const dateObj = new Date(parseInt(dateYear, 10), parseInt(dateMonth, 10) - 1);
      if (dateObj < minDate) {
        errors[date.label as keyof T] = `Date must be after ${minDate.getFullYear() - 1}`;
      }
    }

    if (dateYear && dateMonth) {
      const dateObj = new Date(parseInt(dateYear, 10), parseInt(dateMonth, 10) - 1);
      if (dateObj > maxDate) {
        errors[date.label as keyof T] = `Date must be before ${maxDate.getFullYear() + 1}`;
      }
    }
  });

  return errors;
};

export const isDateRangeValid = <T extends { endDate?: string | undefined; startDate?: string | undefined }>(
  dateRange: DateRange,
): Partial<FormStateErrors<T>> => {
  const errors = {} as Partial<FormStateErrors<T>>;
  let errorTextEndDate = '';
  let errorTextStartDate = '';

  const [startDate, endDate] = dateRange;

  if (startDate && endDate && isDateValid(startDate) && isDateValid(endDate)) {
    const [startMonth, startYear] = startDate.split('/');
    const [endMonth, endYear] = endDate.split('/');

    if (parseInt(startYear, 10) > parseInt(endYear, 10)) {
      errorTextEndDate = 'End date must be after start date';
      errorTextStartDate = 'Start date must be before end date';
    } else if (parseInt(startYear, 10) === parseInt(endYear, 10) && parseInt(startMonth, 10) > parseInt(endMonth, 10)) {
      errorTextEndDate = 'End date must be after start date';
      errorTextStartDate = 'Start date must be before end date';
    }

    if (errorTextEndDate) errors.endDate = errorTextEndDate;
    if (errorTextStartDate) errors.startDate = errorTextStartDate;
  } else {
    if ((!startDate || !isDateValid(startDate)) && errors.endDate?.includes('after start date')) {
      errors.endDate = '';
    }

    if ((!endDate || !isDateValid(endDate)) && errors.startDate?.includes('before end date')) {
      errors.startDate = '';
    }
  }

  return errors;
};

export const formatErrorText = (
  fieldName: string,
  error: string | undefined,
  existingErrors: Record<string, string>,
) => {
  let errorText = '';

  if (error === 'minDate') {
    errorText = `Date must be after ${minDate.getFullYear() - 1}`;
  } else if (error === 'maxDate') {
    errorText = `Date must be before ${maxDate.getFullYear() + 1}`;
  } else if (error === 'invalidDate') {
    errorText = 'Please enter a valid date';
  }

  let errorValue: string | undefined = undefined;

  if (errorText || errorText === '') {
    errorValue = errorText;
  } else if (error) {
    errorValue = formHelperText[fieldName];
  }

  const errors = { ...existingErrors };

  if (!errorValue) {
    errors[fieldName] = '';
  } else {
    errors[fieldName] = errorValue;
  }

  return errors;
};

export const dateValidations = <T>(dates: DateField[], dateRange: DateRange): Partial<FormStateErrors<T>> => {
  return {
    ...validateMonthYearDates(dates),
    ...isDateRangeValid(dateRange),
  } as Partial<FormStateErrors<T>>;
};
